import { Injectable } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import { ApiService } from 'shared/services/api/api.service';
import { AppStateService } from 'shared/services/app-state/app-state.service';
import { CategoryType, StaticData, ViewValue } from 'shared/models/global';
import { MusicCategory } from '../../models/music.model';
import { ResidentPreferenceData, ResidentPreferenceDataRaw } from '@resparke/models';
import { Resident } from '@resparke/models/client-types';

@Injectable({
  providedIn: 'root'
})
export class ResidentService {

  constructor(
    private apiService: ApiService,
    private appState: AppStateService,
  ) { }

  getCategories(input: {
    slug: string,
    type: CategoryType,
  }): Observable<ViewValue[]> {

    const statement = `
      query getCategories($input: CategoryInput) {
        getCategories(input: $input) {
          category
          slug
          sort
        }
      }
    `;
    return this.apiService
      .graphql<MusicCategory[]>({ statement, variables: { input }, type: 'getCategories' })
      .pipe(
        map(result => result.map(val => ({ viewValue: val.category, value: val.slug })).sort((a, b) => a.viewValue < b.viewValue ? -1 : 1))
      )
  }

  getResident(input: { facilityId: string, residentId: string }): Observable<Resident> {
    const statement = `
      query getResident($input: GetResidentInput!) {
        getResident(input: $input) {
          id
          firstName
          lastName
          preferredName
          roomNumber
          preferenceId
          program
          family {
            id
            firstName
            lastName
            confirmed
          }
        }
      }
    `;
    return this.apiService
      .graphql<Resident>({ statement, variables: { input }, type: 'getResident' })
      .pipe(
        tap(resident => {
          const user = this.appState.get('currentUser');
          const familyMember = resident.family.find(f => f.id === user.id);
          this.appState.set('residentFamily', familyMember);
          this.appState.set('currentResident', resident);
        })
      )
  }

  getStaticData(input: { items: string[]}): Observable<ViewValue[]> {
    const statement = `
      query getStaticData($input: StaticDataInput!) {
          getStaticData(input: $input) {
            name
            data
          }
        }
    `;
    return this.apiService
      .graphql<StaticData[]>({ statement, variables: { input }, type: 'getStaticData' } )
      .pipe(
        map<StaticData[], ViewValue[]>(res => {
          if (!res.length) {
            return [];
          } else if (res.length === 1) {
            return JSON.parse(res[0].data)
          }
        })
      )
  }

  getArtists(): Observable<ViewValue[]> {
    const statement = `
      query getArtists {
        getArtists {
          viewValue
          value
        }
      }
    `;
    return this.apiService
      .graphql<ViewValue[]>({ statement, variables: {}, type: 'getArtists' })
      .pipe(
        map(artists => artists.sort((a, b) => a.viewValue > b.viewValue ? 1 : -1))
      )

  }

  getResidentPreferenceData(preferenceId: string): Observable<boolean> {
    const statement = `
      query getResidentPreferenceData($preferenceId: ID!) {
        getResidentPreferenceData(preferenceId: $preferenceId) {
          id
          profile {
            gender
            yob
          }
          music {
            genres
            favouriteArtists
            suggestedArtists
            memories
          }
          occupationsLife {
            industries
            suggestedOccupations
            memories
            achievements
          }
          languageCountry {
            primaryLanguage
            secondaryLanguage
            countryOfBirth
            countryGrewUp
            countryOtherPlaces
          }
          cultureValuesBelieves {
            religion
            suggestedReligion
            practiceReligion
            culturalBackground
            personalIdentity
            communityGroups
          }
          peoplePets {
            pets
            animals
            importantPeople {
              name
              relationship
            }
          }
          hobbiesInterestsSports {
            hobbiesInterests
            hobbiesMemories
            sports
            suggestedSports
            sportsMemories
          }

          conversationStarters {
            conversationStarters
          }
        }
      }
    `;
    return this.apiService
      .graphql<ResidentPreferenceDataRaw>({ statement, variables: { preferenceId }, type: 'getResidentPreferenceData' })
      .pipe(
        map(data => {
          const res: ResidentPreferenceData = {
            id: data.id,
            profile: data.profile.length ? data.profile[0]: undefined,
            music: data.music.length ? data.music[0]: undefined,
            cultureValuesBelieves: data.cultureValuesBelieves.length ? data.cultureValuesBelieves[0]: undefined,
            hobbiesInterestsSports: data.hobbiesInterestsSports.length ? data.hobbiesInterestsSports[0]: undefined,
            languageCountry: data.languageCountry.length ? data.languageCountry[0]: undefined,
            peoplePets: data.peoplePets.length ? data.peoplePets[0]: undefined,
            occupationsLife: data.occupationsLife.length ? data.occupationsLife[0]: undefined,
            conversationStarters: data.conversationStarters.length ? data.conversationStarters[0]: undefined,
          }
          this.appState.set('currentResidentPreferenceData', res);
          return true;
        })
      )
  }
}
